
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MatchData, SavedState } from '@/components/team-details/types/selector-types';

interface CompareTeamsParams {
  homeTeam: string;
  awayTeam: string;
  league: string;
  years: string[];
}

export const useCompareTeams = ({ homeTeam, awayTeam, years }: Omit<CompareTeamsParams, 'league'>) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['matches', years, homeTeam, awayTeam],
    queryFn: async () => {
      console.log('Starting data fetch for:', { years, homeTeam, awayTeam });
      
      if (years.length === 0 || !homeTeam || !awayTeam) {
        throw new Error('Missing required parameters');
      }

      const allMatches: MatchData[] = [];
      const sortedYears = [...years].sort((a, b) => parseInt(a) - parseInt(b));

      const { data: savedStates, error: fetchError } = await supabase
        .from('saved_states')
        .select('*')
        .in('year', sortedYears)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      if (savedStates) {
        savedStates.forEach((state) => {
          if (state.data && Array.isArray(state.data)) {
            const matches = state.data as unknown as MatchData[];
            const seasonMatches = matches.map(match => ({
              ...match,
              seasonYear: state.year || ''
            }));
            allMatches.push(...seasonMatches);
          }
        });
      }

      // Filter matches for each team separately
      const homeTeamMatches = allMatches.filter(match => 
        match.HomeTeam === homeTeam || match.AwayTeam === homeTeam
      );

      const awayTeamMatches = allMatches.filter(match => 
        match.HomeTeam === awayTeam || match.AwayTeam === awayTeam
      );

      // Sort matches chronologically
      const sortMatches = (matches: MatchData[]) => {
        return matches.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
      };

      return {
        homeTeamMatches: sortMatches(homeTeamMatches),
        awayTeamMatches: sortMatches(awayTeamMatches)
      };
    },
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache persists for 30 minutes
  });
};
